import { ApplicationElement, html } from '@effectivastudio/lit-components';

import translations from './translations.js';
import style from './style.lit.css';

/**
 * Link to a folder. A different icon is shown depending on the layout and presence of `imgUrl` property.
 *
 * @prop createdAt - Folder's creation date
 * @prop ownerInfo - Name of the folder owner (optional)
 * @prop sizeInfo - Size of the folder contents (optional)
 * @prop imgUrl - Url of the image next to the folder icon (**pass only to top level folders**) (optional)
 * @prop url - URL to the folders contents
 * @prop layout - The layout of the element (options: thumb, list)
 *
 * @slot default - Folder title
 */

export default class Folder extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    createdAt: { type: Date },
    sizeInfo: { type: String },
    ownerInfo: { type: String },
    imgUrl: { type: String },
    url: { type: String },
    layout: { type: String, reflect: true },
  };

  constructor() {
    super();
    this.layout = 'thumb';
  }

  /** @internal */
  get isTopLevel() {
    return !!this.imgUrl;
  }

  /** @internal */
  get icon() {
    if (this.isTopLevel)
      return this.layout === 'list'
        ? 'folder-user:solid'
        : 'folder-closed:solid';

    return 'folder:solid';
  }

  /** @internal */
  get content() {
    if (this.layout === 'list') {
      if (this.isTopLevel) return this.renderTopLevelList();

      return this.renderList();
    }

    return this.renderThumb();
  }

  renderThumb() {
    return html`
      <div class="figure-wrapper">
        <bu-icon name=${this.icon}></bu-icon>
        ${this.isTopLevel ? html`<img src=${this.imgUrl} />` : null}
      </div>
      <slot></slot>
    `;
  }

  renderTopLevelList() {
    return html`
      <bu-icon name=${this.icon}></bu-icon>
      <slot></slot>
    `;
  }

  renderList() {
    return html`
      <bu-icon name=${this.icon}></bu-icon>
      <div>
        <slot></slot>
        <span class="owner">${this.ownerInfo}</span>
        <span class="created-at">
          <span>${this.t('created_at')}</span>
          ${this.i18n.strftime(this.createdAt, '%d %b %Y')}
        </span>
        <span class="size">${this.sizeInfo}</span>
      </div>
    `;
  }

  render() {
    return html`
      <a
        href=${this.url}
        class=${this.isTopLevel ? 'level-top' : 'level-inner'}>
        ${this.content}
      </a>
    `;
  }
}

Folder.registerTagName('bu-folder');

customElements.get('bu-folder') || customElements.define('bu-folder', Folder);
